import React, { useState } from 'react';
import PressableScale from './motion/PressableScale';
import { View, Text, StyleSheet, Modal, TouchableOpacity, ScrollView, Image, Platform , Pressable } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { BlurView } from 'expo-blur';
import GradientButton from './GradientButton';
import * as betMath from '../utils/betMath';

const BetSlipModal = ({ visible, onClose, onSubmit, selectedOptions = {}, selectedLegs = [], stake = 100 }) => {
    // State for each leg's expanded sections (keyed by leg id)
    const [expandedMath, setExpandedMath] = useState({});
    const [expandedBooks, setExpandedBooks] = useState({});
    
    // Helpers for odds math
    const americanToDecimal = (a) => betMath.americanToDecimal(a);
    const getLegDecimal = (leg) => {
        try {
            const raw = leg?.raw || {};
            const price = raw.price ?? raw.american_price ?? raw.odds ?? null;
            const dec = americanToDecimal(price);
            return dec || 1.91; // default ~-110 if missing
        } catch { return 1.91; }
    };
    const calculateTotalWinnings = () => {
        try {
            if (Array.isArray(selectedLegs) && selectedLegs.length) {
                const combined = selectedLegs.map(getLegDecimal).reduce((a, b) => a * b, 1);
                const payout = stake * combined;
                return Math.round(payout);
            }
            return stake;
        } catch { return stake; }
    };

    // Calculate combined SGP math
    const calculateCombinedMath = () => {
        if (!selectedLegs || selectedLegs.length < 2) return null;
        
        const legs = selectedLegs.map(leg => {
            const price = leg?.raw?.price ?? leg?.raw?.american_price ?? null;
            const aiProb = leg?.raw?.ai_prob ?? leg?.ai_prob ?? null;
            let implied = null;
            if (typeof price === 'number' && !isNaN(price)) {
                implied = betMath.americanToImplied(price);
            }
            const decimal = americanToDecimal(price);
            return { price, aiProb, implied, decimal };
        });
        
        const independentProb = legs.every(l => l.aiProb) 
            ? legs.reduce((acc, l) => acc * l.aiProb, 1) 
            : null;
        
        const combinedDecimal = legs.every(l => l.decimal) 
            ? legs.reduce((acc, l) => acc * l.decimal, 1) 
            : null;
        
        const combinedAmerican = combinedDecimal 
            ? (combinedDecimal >= 2 ? Math.round((combinedDecimal - 1) * 100) : Math.round(-100 / (combinedDecimal - 1)))
            : null;
        
        const payout = combinedDecimal ? stake * combinedDecimal : null;
        const profit = payout ? payout - stake : null;
        
        const ev = (independentProb && combinedDecimal) 
            ? (independentProb * combinedDecimal * stake - stake) 
            : null;
        const evPercent = ev !== null ? ((ev / stake) * 100) : null;
        
        return { independentProb, combinedAmerican, payout, profit, ev, evPercent };
    };

    // Render cards for fetched selected legs
    const renderSelectedLegCard = (leg, idx) => {
        const legId = leg.id || idx;
        const showMath = expandedMath[legId] || false;
        const showBooks = expandedBooks[legId] || false;
        
        const price = leg?.raw?.price ?? leg?.raw?.american_price ?? leg?.raw?.odds ?? null;
        const book = (leg?.raw?.sportsbook || leg?.raw?.book || '');
        const urls = leg?.raw?.urls || {};
        const deeplink = urls.offer || urls.deeplink || urls.event || urls.market || null;
        
        // Math calculations
        const implied = (() => {
            if (price === null || isNaN(price)) return null;
            if (price > 0) return 100 / (price + 100);
            return (-price) / (-price + 100);
        })();
        const aiProb = leg?.raw?.ai_prob ?? leg?.ai_prob ?? null;
        const ev = (aiProb !== null && implied !== null) ? ((aiProb - implied) * 100) : null;
        
        return (
            <View key={legId} style={styles.betCardContainer}>
                <Text style={styles.legTitle}>
                    Leg {idx + 1} • {leg.title}
                </Text>
                <View style={styles.betCard}>
                    <LinearGradient
                        colors={['rgba(0, 0, 0, 0.16)', 'rgba(116, 132, 154, 0.16)']}
                        style={styles.betCardGradient}
                    >
                        <BlurView intensity={90} tint="dark" style={styles.betCardBlur}>
                            <View style={styles.betCardContent}>
                                <View style={styles.betCardHeader}>
                                    <View style={styles.betCardInfo}>
                                        <Text style={styles.betCardTitle}>{leg.description || ''}</Text>
                                        <Text style={styles.betCardDescription}>{book ? `Book: ${book}` : ''}{price!==null?`  •  ${price}`:''}</Text>
                                    </View>
                                </View>
                                
                                {showMath && (
                                    <View style={{ alignSelf: 'stretch', backgroundColor: 'rgba(255,255,255,0.04)', padding: 12, borderRadius: 8, marginTop: 8, gap: 8 }}>
                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>Price:</Text>
                                            <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>{price ?? '—'}</Text>
                                        </View>
                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>Implied Prob:</Text>
                                            <Text style={{ color: '#fff', fontSize: 13 }}>{implied !== null ? `${(implied * 100).toFixed(1)}%` : '—'}</Text>
                                        </View>
                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>Market Avg:</Text>
                                            <Text style={{ color: '#fff', fontSize: 13 }}>{aiProb !== null ? `${(aiProb * 100).toFixed(1)}%` : '—'}</Text>
                                        </View>
                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>EV%:</Text>
                                            <Text style={{ color: ev !== null ? (ev >= 0 ? '#4ade80' : '#f87171') : '#fff', fontSize: 13, fontWeight: '700' }}>
                                                {ev !== null ? `${ev >= 0 ? '+' : ''}${ev.toFixed(1)}%` : '—'}
                                            </Text>
                                        </View>
                                    </View>
                                )}
                                
                                {showBooks && Array.isArray(leg.availableBooks) && leg.availableBooks.length ? (
                                    <View style={{ alignSelf: 'stretch', backgroundColor: 'rgba(255,255,255,0.04)', padding: 12, borderRadius: 8, marginTop: 8 }}>
                                        <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, marginBottom: 6 }}>Available at:</Text>
                                        <Text style={{ color: '#fff', fontSize: 13 }}>{leg.availableBooks.join(', ')}</Text>
                                    </View>
                                ) : null}
                                
                                <View style={styles.betCardActions}>
                                    {deeplink ? (
                                        <View>
                                            <GradientButton
                                                text="View on Sportsbook"
                                                onPress={() => { try { if (Platform.OS === 'web') window.open(deeplink, '_blank'); } catch {} }}
                                                style={styles.betCardButton}
                                                gradientColors={["#707070", "#EFEFEF"]}
                                            />
                                        </View>
                                    ) : null}
                                    <View>
                                        <GradientButton
                                            text={showBooks ? "Hide Books" : "Books"}
                                            onPress={() => setExpandedBooks(prev => ({ ...prev, [legId]: !prev[legId] }))}
                                            style={styles.betCardButton}
                                            gradientColors={["#707070", "#EFEFEF"]}
                                        />
                                    </View>
                                    <View>
                                        <GradientButton
                                            text={showMath ? "Hide Math" : "Show Math"}
                                            onPress={() => setExpandedMath(prev => ({ ...prev, [legId]: !prev[legId] }))}
                                            style={styles.betCardButton}
                                            gradientColors={["#707070", "#EFEFEF"]}
                                        />
                                    </View>
                                </View>
                            </View>
                        </BlurView>
                    </LinearGradient>
                </View>
            </View>
        );
    };

    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="fade"
            onRequestClose={onClose}
        >
            <View style={styles.modalOverlay}>
                <Pressable
                    style={StyleSheet.absoluteFill}
                    accessibilityLabel="Close bet slip"
                    onPress={onClose}
                />
                <View style={styles.modalContainer}>
                    <BlurView intensity={60} tint="dark" style={styles.modalBlur}>
                        <View style={styles.modalContent}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 8 }}>
                                <Image source={require('../assets/icon.png')} style={{ width: 32, height: 32, borderRadius: 6 }} resizeMode="contain" />
                                <Text style={styles.modalTitle}>Review Your Bet Slip</Text>
                                <PressableScale
                                    onPress={onClose}
                                    style={{ position: 'absolute', right: 0, top: 0, padding: 8, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.1)' }}
                                >
                                    <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 18, fontWeight: '700', lineHeight: 20 }}>✕</Text>
                                </PressableScale>
                            </View>
                            
                            <ScrollView 
                                style={styles.betSlipContent} 
                                contentContainerStyle={styles.betSlipContentContainer}
                                showsVerticalScrollIndicator={false}
                                bounces={false}
                            >
                                {Array.isArray(selectedLegs) && selectedLegs.length > 0 ? (
                                    selectedLegs.map((leg, index) => renderSelectedLegCard(leg, index))
                                ) : (
                                    <View style={{ paddingVertical: 40, alignItems: 'center' }}>
                                        <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 15 }}>No legs selected</Text>
                                    </View>
                                )}
                            </ScrollView>
                            
                            {(() => {
                                const math = calculateCombinedMath();
                                if (!math) {
                                    return (
                                        <Text style={styles.totalWinnings}>
                                            To Win ${calculateTotalWinnings()}
                                        </Text>
                                    );
                                }
                                return (
                                    <View style={{ alignSelf: 'stretch', paddingVertical: 12, paddingHorizontal: 16, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.05)', gap: 8 }}>
                                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>Combined Odds:</Text>
                                            <Text style={{ color: '#fff', fontSize: 16, fontWeight: '700' }}>
                                                {math.combinedAmerican !== null ? `${math.combinedAmerican > 0 ? '+' : ''}${math.combinedAmerican}` : '—'}
                                            </Text>
                                        </View>
                                        {math.independentProb !== null ? (
                                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>Independent Win Prob:</Text>
                                                <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14 }}>
                                                    {(math.independentProb * 100).toFixed(2)}%
                                                </Text>
                                            </View>
                                        ) : null}
                                        {math.payout !== null ? (
                                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>Potential Payout:</Text>
                                                <Text style={{ color: '#4ade80', fontSize: 16, fontWeight: '700' }}>
                                                    ${math.payout.toFixed(2)}
                                                </Text>
                                            </View>
                                        ) : null}
                                        {math.profit !== null ? (
                                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>Profit:</Text>
                                                <Text style={{ color: '#4ade80', fontSize: 14, fontWeight: '600' }}>
                                                    ${math.profit.toFixed(2)}
                                                </Text>
                                            </View>
                                        ) : null}
                                        {math.evPercent !== null ? (
                                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>Expected Value (EV):</Text>
                                                <Text style={{ color: math.evPercent >= 0 ? '#4ade80' : '#f87171', fontSize: 14, fontWeight: '700' }}>
                                                    {math.evPercent >= 0 ? '+' : ''}{math.evPercent.toFixed(2)}%
                                                </Text>
                                            </View>
                                        ) : null}
                                        <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginTop: 4 }}>
                                            Note: EV calculated using independent probability. Real SGP correlation may differ.
                                        </Text>
                                    </View>
                                );
                            })()}
                            
            <View style={styles.modalActions}>
                <PressableScale
                    onPress={() => {
                        console.log('BetSlipModal: Go Back button pressed');
                        onClose();
                    }}
                    style={styles.goBackButton}
                >
                    <Text style={styles.goBackButtonText}>Go Back</Text>
                </PressableScale>
                <GradientButton
                    text="Submit Betslip"
                    onPress={() => {
                        console.log('Submitting bet slip...');
                        onClose(); // Always close modal first
                        if (onSubmit) {
                            setTimeout(() => onSubmit(), 100); // Delay to ensure modal closes
                        }
                    }}
                    style={styles.submitButton}
                />
            </View>
                        </View>
                    </BlurView>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        justifyContent: 'center',
        alignItems: 'center',
        ...(Platform.OS === 'web' && { 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
        }),
    },
    modalContainer: {
        width: 800,
        minHeight: 600,
        maxHeight: '90vh',
        borderRadius: 20,
        overflow: 'hidden',
        shadowColor: '#fff',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 50,
        elevation: 20,
    },
    modalBlur: {
        flex: 1,
        borderRadius: 20,
    },
    modalContent: {
        flex: 1,
        padding: 48,
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 20,
        minHeight: 0, // Allow flex shrinking
    },
    modalTitle: {
        alignSelf: 'stretch',
        textAlign: 'center',
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: 20,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 24,
    },
    betSlipContent: {
        alignSelf: 'stretch',
        flex: 1,
        minHeight: 0, // Allow flex shrinking
    },
    betSlipContentContainer: {
        flexGrow: 1, // Allow content to grow and fill available space
        justifyContent: 'flex-start',
    },
    betCardContainer: {
        alignSelf: 'stretch',
        marginBottom: 10,
    },
    legTitle: {
        alignSelf: 'stretch',
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: 16,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 24,
        marginBottom: 5,
    },
    betCard: {
        alignSelf: 'stretch',
        borderRadius: 20,
        overflow: 'hidden',
        borderWidth: 1,
        borderTopColor: 'rgba(255, 255, 255, 0.25)',
        borderLeftColor: 'rgba(255, 255, 255, 0.18)',
        borderRightColor: 'rgba(255, 255, 255, 0.12)',
        borderBottomColor: 'rgba(255, 255, 255, 0.08)',
    },
    betCardGradient: {
        flex: 1,
    },
    betCardBlur: {
        flex: 1,
        padding: 16,
    },
    betCardContent: {
        flex: 1,
        gap: 32,
    },
    betCardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 8,
    },
    betCardInfo: {
        flex: 1,
        gap: 8,
    },
    betCardTitle: {
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 20,
    },
    betCardDescription: {
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '400',
        lineHeight: 20,
    },
    betCardAmount: {
        color: '#707070',
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 20,
    },
    betCardActions: {
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'flex-end',
        gap: 8,
    },
    betCardButton: {
        flex: 1,
        marginRight: 8,
    },
    noSelectionText: {
        color: 'rgba(255, 255, 255, 0.40)',
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '400',
        lineHeight: 20,
        textAlign: 'center',
        padding: 20,
    },
    totalWinnings: {
        alignSelf: 'stretch',
        textAlign: 'right',
        color: '#749A77',
        fontSize: 20,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 24,
    },
    modalActions: {
        width: 319,
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 20,
    },
    goBackButton: {
        backgroundColor: 'rgba(255, 78.60, 78.60, 0.15)',
        paddingHorizontal: 20,
        paddingVertical: 12,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
    },
    goBackButtonText: {
        color: 'rgba(255, 78.60, 78.60, 0.9)',
        fontSize: 16,
        fontWeight: '600',
    },
    submitButton: {
        backgroundColor: 'rgba(116, 154, 119, 0.15)',
    },
});

export default BetSlipModal;
